import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../../services/api';
import MedicoCard from '../../components/public/MedicoCard';
import MediciPagination from '../../components/public/MediciPagination';
import PublicSEO from '../../components/public/PublicSEO';
import { getMedicoSlug } from '../../utils/slug';
import { publicPath } from '../../utils/canonicalUrl';
import { getSnapApiDataByPrefix } from '../../utils/snapStore';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { MEDICI_PAGE_SIZE, paginateList, sortMediciByCognome } from '../../utils/mediciList';
import {
  buildBreadcrumb,
  buildCollectionPage,
  buildMediciItemList,
} from '../../utils/jsonLd';
import './MediciPage.css';

function parsePage(raw) {
  const n = parseInt(raw || '1', 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/** Filtra lato client se l'API non ha applicato branca (idBranche in JSON). */
function filterByBranca(list, brancaId) {
  if (!brancaId) return list;
  const idNum = Number(brancaId);
  return list.filter((m) => {
    const ids = m.idBranche || m.id_branche || [];
    if (!Array.isArray(ids) || ids.length === 0) return false;
    return ids.some((x) => Number(x) === idNum || String(x) === String(brancaId));
  });
}

const MediciPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const brancaId = searchParams.get('branca') || '';
  const qParam = searchParams.get('q') || '';
  const page = parsePage(searchParams.get('page'));

  const snapMedici = getSnapApiDataByPrefix('/public/medici');
  const initialMedici = Array.isArray(snapMedici)
    ? snapMedici
    : (snapMedici?.content && Array.isArray(snapMedici.content) ? snapMedici.content : []);

  const [medici, setMedici] = useState(() =>
    sortMediciByCognome(brancaId ? filterByBranca(initialMedici, brancaId) : initialMedici));
  const [branche, setBranche] = useState([]);
  const [loading, setLoading] = useState(initialMedici.length === 0);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState(qParam);
  const [reloadToken, setReloadToken] = useState(0);

  const debouncedSearch = useDebouncedValue(searchInput, 300);

  useEffect(() => {
    setSearchInput(qParam);
  }, [qParam]);

  useEffect(() => {
    api.get('/public/branche')
      .then((res) => setBranche(Array.isArray(res.data) ? res.data : []))
      .catch(() => setBranche([]));
  }, []);

  // Ricerca debounced → URL (canonical SEO resta /medici; query solo filtri UI)
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const q = debouncedSearch.trim();
    if (q) next.set('q', q);
    else next.delete('q');
    if (next.get('q') !== searchParams.get('q')) {
      next.delete('page');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const setBranca = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('branca', value);
    else next.delete('branca');
    next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const setPage = (p) => {
    const next = new URLSearchParams(searchParams);
    if (p > 1) next.set('page', String(p));
    else next.delete('page');
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      const q = qParam.trim();
      let url;
      if (brancaId) {
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        url = `/public/branche/${brancaId}/medici${params.toString() ? `?${params}` : ''}`;
      } else {
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        url = `/public/medici${params.toString() ? `?${params}` : ''}`;
      }

      const tryFetch = async () => {
        const response = await api.get(url);
        const data = response.data;
        let list = Array.isArray(data) ? data : (data?.content && Array.isArray(data.content) ? data.content : []);
        return sortMediciByCognome(filterByBranca(list, brancaId));
      };

      try {
        let list;
        try {
          list = await tryFetch();
        } catch (firstErr) {
          // Retry una volta (Tomcat/nginx a volte rispondono 404 durante redeploy)
          await new Promise((r) => setTimeout(r, 400));
          if (cancelled) return;
          list = await tryFetch();
        }
        if (!cancelled) {
          setMedici(list);
          setError(null);
        }
      } catch (err) {
        console.error('Errore caricamento medici:', err);
        if (!cancelled) {
          setMedici((prev) => {
            if (prev.length === 0) {
              setError(err.response?.data?.message || 'Impossibile caricare l\'elenco medici. Riprova.');
            }
            return prev;
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [brancaId, qParam, reloadToken]);

  const paged = useMemo(
    () => paginateList(medici, page, MEDICI_PAGE_SIZE),
    [medici, page]
  );

  useEffect(() => {
    if (paged.totalPages > 0 && page > paged.totalPages) {
      setPage(paged.totalPages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paged.totalPages, page]);

  if (loading && medici.length === 0 && !error) {
    return (
      <div className="medici-page">
        <PublicSEO title="Medici e specialisti online" canonicalPath="/medici" />
        <div className="container medici-page-loading-shell" aria-busy="true" aria-label="Caricamento medici" />
      </div>
    );
  }

  if (error && medici.length === 0) {
    return (
      <div className="medici-page">
        <PublicSEO title="Medici e specialisti online" canonicalPath="/medici" />
        <div className="container">
          <div className="medici-page-error">
            <p className="medici-page-error-text">{error}</p>
            <button
              type="button"
              className="vc-btn vc-btn-primary"
              onClick={() => setReloadToken((n) => n + 1)}
            >
              Riprova
            </button>
          </div>
        </div>
      </div>
    );
  }

  const jsonLd = [
    buildCollectionPage({
      canonicalPath: '/medici',
      name: 'Medici e specialisti per consulti online',
      description: 'Elenco dei medici e specialisti disponibili per visite online su Virtual Clinic.',
    }),
    buildBreadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Medici' },
    ]),
    ...(paged.items.length > 0 ? [buildMediciItemList(paged.items, getMedicoSlug)] : []),
  ];

  return (
    <div className="medici-page">
      <PublicSEO
        title="Medici e specialisti online - Trova e prenota | Virtual Clinic"
        description="Cerca medici e specialisti per visite online e videoconsulti. Filtra per disciplina, leggi i profili e prenota la consulenza in pochi click."
        canonicalPath="/medici"
        keywords="medici online, specialisti online, telemedicina, prenotazione visita online, medico telemedicina"
        ogImageAlt="Medici e specialisti per consulti online su Virtual Clinic"
        jsonLd={jsonLd}
      />
      <div className="container medici-page-container">
        <h1 className="medici-page-title">Medici e specialisti per consulti online</h1>
        <p className="medici-page-intro">
          Trova il medico o lo specialista adatto a te. Filtra per disciplina o cerca per nome.
          Puoi anche sfogliare le <Link to={publicPath('/discipline')}>discipline</Link>.
        </p>

        <div className="filters-top medici-filters">
          <div className="medici-filter-branca">
            <label className="medici-filter-label" htmlFor="medici-branca">Disciplina</label>
            <select
              id="medici-branca"
              className="form-control"
              value={brancaId}
              onChange={(e) => setBranca(e.target.value)}
            >
              <option value="">Tutte le discipline</option>
              {branche.map((b) => (
                <option key={b.id} value={b.id}>
                  {(b.branca || '').replace(/^./, (c) => c.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
          <div className="medici-filter-search">
            <label className="medici-filter-label" htmlFor="medici-q">Cerca</label>
            <input
              id="medici-q"
              type="search"
              className="form-control"
              placeholder="Nome, cognome o specializzazione..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        {loading && medici.length > 0 && (
          <p className="medici-page-updating" aria-live="polite">Aggiornamento elenco…</p>
        )}

        <div className="medici-grid" role="list">
          {paged.items.map((medico) => (
            <MedicoCard
              key={medico.id}
              medico={medico}
              to={publicPath(`/medici/${getMedicoSlug(medico)}`)}
            />
          ))}
        </div>

        {medici.length === 0 && !loading && (
          <p className="medici-page-empty">Nessun medico trovato per i filtri selezionati.</p>
        )}

        <MediciPagination
          page={paged.page}
          totalPages={paged.totalPages}
          total={paged.total}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
};

export default MediciPage;
